import React, { useMemo, useState } from "react";
import { Add, DeleteOutline, EditOutlined } from "@mui/icons-material";
import { Alert, Box, Divider, IconButton, Paper, Tooltip, Typography } from "@mui/material";
import DataTable from "../../../components/common/tables/DataTable";
import EntityFormDialog from "../components/EntityFormDialog";
import SectionCard from "../components/SectionCard";
import { subcontractApi } from "../services/subcontractApi";
import { AppButton } from "components/common/buttons";
import AppSnackbar from "components/common/snackbar/AppSnackbar";
import { refreshAfterSave } from "utils/workflowOperations";
import { calculateAmount } from "../../contracts/utils/commercialCalculations";
import useSubcontractPermissions, { SUBCONTRACT_PERMISSIONS } from "../hooks/useSubcontractPermissions";
import { formatCurrencyWithUnit, formatAmount, formatDate } from "utils/formatters";
import {
  getLatestApprovalProfile,
  isApprovedStatus as isApprovedProfileStatus,
} from "../utils/subcontracts/approvalStatus";

const itemLabel = (item) => {
  const baseLabel = `${item.itemCode || ""} - ${item.itemName || ""}`.replace(/^- | - $/, "");
  return item.difficultyLevel == null ? baseLabel : `${baseLabel} (KK ${item.difficultyLevel})`;
};
const normalize = (value) => String(value || "").trim().toUpperCase();
const getId = (value, key) => value?.[key] ?? value?.[`${key[0].toUpperCase()}${key.slice(1)}`];
const itemIdOf = (item) => item?.itemId ?? item?.ItemId;
const getScopeId = (value) => value?.subcontractItemScopeId ?? value?.SubcontractItemScopeId ?? value?.scopeId;
const getItemCodeParts = (code) =>
  String(code || "")
    .split(".")
    .map((part) => part.trim())
    .filter(Boolean);
const compareItemCodeParts = (leftPart = "", rightPart = "") => {
  const leftIsNumber = /^\d+$/.test(leftPart);
  const rightIsNumber = /^\d+$/.test(rightPart);

  if (leftIsNumber && rightIsNumber) {
    return Number(leftPart) - Number(rightPart);
  }

  return leftPart.localeCompare(rightPart, "vi", {
    numeric: true,
    sensitivity: "base",
  });
};
const compareItemCode = (left, right) => {
  const leftParts = getItemCodeParts(left?.itemCode ?? left?.ItemCode);
  const rightParts = getItemCodeParts(right?.itemCode ?? right?.ItemCode);
  const maxLength = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < maxLength; index += 1) {
    const comparison = compareItemCodeParts(leftParts[index], rightParts[index]);
    if (comparison !== 0) return comparison;
  }

  return Number(itemIdOf(left) || 0) - Number(itemIdOf(right) || 0);
};
const sortItemsByCode = (items = []) => [...items].sort(compareItemCode);
const sortScopeGroups = (groups = []) => [...groups].sort((left, right) =>
  Number(left.displayOrder ?? 0) - Number(right.displayOrder ?? 0)
  || String(left.label || "").localeCompare(String(right.label || ""), "vi", { sensitivity: "base" }),
);
const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export default function PaymentsTab({
  subcontractId,
  subcontract = null,
  payments = [],
  items = [],
  sections = [],
  subcontractApprovalProfiles = [],
  onChanged,
}) {
  const permissions = useSubcontractPermissions();
  const [dialog, setDialog] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const canCreate = permissions.has(SUBCONTRACT_PERMISSIONS.paymentCreate);
  const canUpdate = permissions.has(SUBCONTRACT_PERMISSIONS.paymentUpdate);
  const canDelete = permissions.has(SUBCONTRACT_PERMISSIONS.paymentDelete);
  const latestSubcontractApprovalProfile = useMemo(
    () => getLatestApprovalProfile(subcontractApprovalProfiles),
    [subcontractApprovalProfiles],
  );
  const isSubcontractApproved = isApprovedProfileStatus(
    latestSubcontractApprovalProfile?.status ?? latestSubcontractApprovalProfile?.Status,
  );
  const allPaymentItems = useMemo(
    () => payments.flatMap((payment) => payment.items || []),
    [payments],
  );

  const findItem = (itemId) => items.find((item) => Number(item.itemId ?? item.ItemId) === Number(itemId));
  const scopeById = useMemo(() => {
    const map = new Map();
    sections.forEach((section) => {
      (section.scopes || []).forEach((scope) => {
        const scopeId = getScopeId(scope);
        if (!scopeId) return;
        map.set(String(scopeId), {
          ...scope,
          sectionId: getId(section, "subcontractSectionId"),
          sectionName: section.sectionName || section.name || section.SectionName,
          sectionType: section.sectionType || section.SectionType,
          sectionDisplayOrder: section.displayOrder ?? section.DisplayOrder ?? 0,
          displayOrder: scope.displayOrder ?? scope.DisplayOrder ?? 0,
        });
      });
    });
    return map;
  }, [sections]);

  const getScopeLabel = (paymentItem) => {
    const scopeType = normalize(paymentItem.scopeType || paymentItem.ScopeType);
    const scopeName = paymentItem.scopeName || paymentItem.ScopeName;
    const provinceName = paymentItem.provinceName || paymentItem.ProvinceName;
    const communeName = paymentItem.communeName || paymentItem.CommuneName;

    if (scopeType === "PROVINCE") return provinceName || scopeName || "Tỉnh/Thành phố";
    if (scopeType === "COMMUNE") return communeName || scopeName || "Xã/Phường";
    if (scopeType === "TOTAL") return scopeName || "Tổng hợp";
    if (scopeType === "ESTIMATE") return scopeName || "Dự toán đơn";
    return scopeName || "Chưa xác định phân cấp";
  };

  const getScopeSubtitle = (paymentItem) => {
    const sectionName = paymentItem.sectionName || paymentItem.SectionName;
    const scopeType = normalize(paymentItem.scopeType || paymentItem.ScopeType);
    const provinceName = paymentItem.provinceName || paymentItem.ProvinceName;

    if (scopeType === "ESTIMATE") {
      return "";
    }
    return sectionName || "";
  };

  const enrichPaymentItem = (paymentItem) => {
    const item = findItem(paymentItem.itemId ?? paymentItem.ItemId);
    const scopeId = getScopeId(paymentItem) || getScopeId(item);
    const scope = scopeId ? scopeById.get(String(scopeId)) : null;

    return {
      ...paymentItem,
      itemCode: paymentItem.itemCode || paymentItem.ItemCode || item?.itemCode || item?.ItemCode,
      itemName: paymentItem.itemName || paymentItem.ItemName || item?.itemName || item?.ItemName,
      unit: paymentItem.unit || paymentItem.Unit || item?.unit || item?.Unit,
      subcontractItemScopeId: scopeId || null,
      scopeId: scopeId || null,
      scopeName: paymentItem.scopeName || paymentItem.ScopeName || scope?.scopeName || scope?.ScopeName || scope?.name,
      scopeType: paymentItem.scopeType || paymentItem.ScopeType || scope?.scopeType || scope?.ScopeType,
      sectionId: paymentItem.subcontractSectionId || paymentItem.SubcontractSectionId || scope?.sectionId || "",
      sectionName: paymentItem.sectionName || paymentItem.SectionName || scope?.sectionName,
      sectionType: paymentItem.sectionType || paymentItem.SectionType || scope?.sectionType,
      provinceId: paymentItem.provinceId || paymentItem.ProvinceId || scope?.provinceId || scope?.ProvinceId,
      provinceName: paymentItem.provinceName || paymentItem.ProvinceName || scope?.provinceName || scope?.ProvinceName,
      communeId: paymentItem.communeId || paymentItem.CommuneId || scope?.communeId || scope?.CommuneId,
      communeName: paymentItem.communeName || paymentItem.CommuneName || scope?.communeName || scope?.CommuneName,
      sectionDisplayOrder: scope?.sectionDisplayOrder ?? 0,
      scopeDisplayOrder: scope?.displayOrder ?? 0,
    };
  };

  const groupPaymentItemsByScope = (paymentItems = []) => {
    const groups = new Map();

    sortItemsByCode(paymentItems.map(enrichPaymentItem)).forEach((paymentItem) => {
      const key = paymentItem.subcontractItemScopeId ? `scope-${paymentItem.subcontractItemScopeId}` : "scope-unknown";
      const current = groups.get(key) || {
        key,
        label: getScopeLabel(paymentItem),
        subtitle: getScopeSubtitle(paymentItem),
        displayOrder: Number(paymentItem.sectionDisplayOrder || 0) * 1000 + Number(paymentItem.scopeDisplayOrder || 0),
        items: [],
      };
      current.items.push(paymentItem);
      groups.set(key, current);
    });

    return sortScopeGroups([...groups.values()]);
  };

  const getScopeOptions = (sectionId) => {
    const section = sections.find((item) => String(getId(item, "subcontractSectionId")) === String(sectionId));
    return (section?.scopes || [])
      .filter((scope) => !(normalize(section?.sectionType) === "COMMUNE_DETAIL" && normalize(scope.scopeType) === "TOTAL"))
      .map((scope) => ({
        value: getId(scope, "subcontractItemScopeId"),
        label: scope.scopeName || scope.name || scope.ScopeName,
      }))
      .filter((item) => item.value != null);
  };
  const getItemOptions = (scopeId) => items
    .filter((item) =>
      !item.hasChildren &&
      (!scopeId || String(item.subcontractItemScopeId ?? item.SubcontractItemScopeId) === String(scopeId))
    )
    .map((item) => ({ value: item.itemId ?? item.ItemId, label: itemLabel(item) }));

  const getProfileTotalBeforeTax = (paymentProfile) =>
    Math.round((paymentProfile?.items || []).reduce(
      (sum, item) => sum + toNumber(item?.paymentAmount ?? item?.PaymentAmount),
      0,
    ));

  const getProfileVatRate = (paymentProfile) =>
    toNumber(paymentProfile?.vat ?? paymentProfile?.VAT ?? subcontract?.vat);

  const getProfileTotalAfterTax = (paymentProfile) => {
    const beforeTax = getProfileTotalBeforeTax(paymentProfile);
    return Math.round(beforeTax * (1 + (getProfileVatRate(paymentProfile) / 100)));
  };

  const subcontractTotalBeforeTax = Math.round(toNumber(subcontract?.totalValue));
  const subcontractVatRate = toNumber(subcontract?.vat);
  const subcontractTotalAfterTax = Math.round(subcontractTotalBeforeTax * (1 + (subcontractVatRate / 100)));
  const totalPaidBeforeTax = Math.round(
    payments.reduce((sum, paymentProfile) => sum + getProfileTotalBeforeTax(paymentProfile), 0),
  );
  const totalPaidAfterTax = Math.round(
    payments.reduce((sum, paymentProfile) => sum + getProfileTotalAfterTax(paymentProfile), 0),
  );
  const remainingBeforeTax = Math.max(subcontractTotalBeforeTax - totalPaidBeforeTax, 0);
  const remainingAfterTax = Math.max(subcontractTotalAfterTax - totalPaidAfterTax, 0);

  const columns = useMemo(
    () => [
      { accessorKey: "paymentName", header: "Kỳ thanh toán" },
      {
        accessorKey: "paymentDate",
        header: "Ngày thanh toán",
        Cell: ({ cell }) => cell.getValue() ? formatDate(cell.getValue()) : "-",
      },
      {
        id: "totalBeforeTax",
        header: "Giá trị thanh toán",
        accessorFn: (row) => getProfileTotalBeforeTax(row),
        Cell: ({ cell }) => formatAmount(cell.getValue()),
      },
    ],
    [payments, subcontract?.vat],
  );

  const itemColumns = [
    {
      id: "item", header: "Dự toán", accessorFn: (row) => itemLabel(findItem(row.itemId) || row),
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    { accessorKey: "paymentQuantity", header: "Khối lượng thanh toán" },
    { accessorKey: "unitPrice", header: "Đơn giá", Cell: ({ cell }) => formatAmount(cell.getValue()) },
    { accessorKey: "paymentAmount", header: "Thành tiền", Cell: ({ cell }) => formatAmount(cell.getValue()) },
    {
      accessorKey: "notes", header: "Ghi chú",
      muiTableBodyCellProps: () => {
        return {
          align: 'left',
        };
      },
    },
    ...(canUpdate || canDelete ? [{
      id: "actions",
      header: "Thao tác",
      size: 80,
      Cell: ({ row }) => (
        <Box>
          {canUpdate && (
            <IconButton
              size="small"
              onClick={() => setDialog({
                type: "detail",
                data: enrichPaymentItem(row.original),
                profile: payments.find((p) => p.paymentProfileId === row.original.paymentProfileId),
              })}
            >
              <EditOutlined fontSize="small" />
            </IconButton>
          )}
          {canDelete && (
            <IconButton size="small" color="error" onClick={async () => { await handleDeletePaymentItem(row.original); }}>
              <DeleteOutline fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    }] : []),
  ];

  const detailIdentityColumns = [
    {
      accessorKey: "itemCode",
      header: "STT",
      accessorFn: (row) => findItem(row.itemId)?.itemCode || findItem(row.itemId)?.ItemCode || row.itemCode || row.ItemCode || "",
    },
    {
      accessorKey: "itemName",
      header: "Dự toán",
      accessorFn: (row) => findItem(row.itemId)?.itemName || findItem(row.itemId)?.ItemName || row.itemName || row.ItemName || "",
    },
  ];

  const saveProfile = async (form) => {
    if (!dialog?.data?.paymentProfileId && !isSubcontractApproved) {
      showSnackbar("Chỉ được thêm kỳ thanh toán khi giao khoán đã được phê duyệt", "error");
      return false;
    }

    const payload = {
      ...form,
      rowVersion: dialog?.data?.rowVersion ?? dialog?.data?.RowVersion,
      subcontractId: Number(subcontractId),
      vat: Number(form.vat),
    };

    if (dialog?.data?.paymentProfileId) await subcontractApi.updatePaymentProfile(dialog.data.paymentProfileId, payload);
    else await subcontractApi.createPaymentProfile(payload);
    if (await refreshAfterSave(onChanged, showSnackbar)) {
      showSnackbar(dialog?.data?.paymentProfileId ? "Cập nhật kỳ thanh toán thành công." : "Tạo kỳ thanh toán thành công.");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const saveItem = async (form) => {
    const selected = findItem(form.itemId);
    const itemId = Number(form.itemId);
    const payload = {
      paymentProfileId: dialog.profile.paymentProfileId,
      itemId,
      paymentSource: "SubcontractItem",
      paymentQuantity: Number(form.paymentQuantity),
      unitPrice: Number(selected?.unitPrice || 0),
      paymentAmount: calculateAmount(form.paymentQuantity, selected?.unitPrice),
      notes: form.notes || null,
    };

    if (dialog?.data?.paymentItemId) await subcontractApi.updatePaymentItem(dialog.data.paymentItemId, payload);
    else await subcontractApi.createPaymentItem(payload);

    await updateItemCompletionPercentage(
      itemId,
      Number(form.paymentQuantity),
      dialog?.data?.paymentItemId,
    );

    if (await refreshAfterSave(onChanged, showSnackbar)) {
      showSnackbar(dialog?.data?.paymentItemId ? "Cập nhật chi tiết thanh toán thành công." : "Tạo chi tiết thanh toán thành công.");
    }
  };

  const updateItemCompletionPercentage = async (itemId, paymentQuantity, editingPaymentItemId = null) => {
    const selectedItem = findItem(itemId);
    const subcontractQuantity = Number(selectedItem?.subcontractQuantity ?? selectedItem?.SubcontractQuantity ?? 0);

    if (!selectedItem || subcontractQuantity <= 0) {
      return;
    }

    let totalPaidQuantity = allPaymentItems
      .filter((payment) => Number(payment.itemId ?? payment.ItemId) === itemId)
      .reduce((sum, payment) => sum + Number(payment.paymentQuantity || payment.PaymentQuantity || 0), 0);

    if (editingPaymentItemId) {
      const previousPayment = allPaymentItems.find(
        (payment) => Number(payment.paymentItemId ?? payment.PaymentItemId) === Number(editingPaymentItemId),
      );
      totalPaidQuantity -= Number(previousPayment?.paymentQuantity || previousPayment?.PaymentQuantity || 0);
    }

    totalPaidQuantity += Number(paymentQuantity || 0);

    const completionPercentage = Math.min(
      100,
      Math.round((totalPaidQuantity / subcontractQuantity) * 100),
    );

    await subcontractApi.updateCompletionPercentage(itemId, completionPercentage);
  };

  const handleDeletePaymentItem = async (paymentItem) => {
    try {
      await subcontractApi.deletePaymentItem(paymentItem.paymentItemId);
      await updateItemCompletionPercentage(
        Number(paymentItem.itemId ?? paymentItem.ItemId),
        0,
        paymentItem.paymentItemId,
      );
      if (await refreshAfterSave(onChanged, showSnackbar)) showSnackbar("Xóa chi tiết thanh toán thành công");
    } catch (error) {
      const message = error?.response?.data?.message || "Không thể xóa chi tiết thanh toán";
      showSnackbar(message, "error");
    }
  };

  const handleDeletePaymentProfile = async (paymentProfileId) => {
    try {
      await subcontractApi.deletePaymentProfile(paymentProfileId);
      if (await refreshAfterSave(onChanged, showSnackbar)) showSnackbar("Xóa kỳ thanh toán thành công");
    } catch (error) {
      const message = error?.response?.data?.message || "Không thể xóa kỳ thanh toán";
      showSnackbar(message, "error");
    }
  };

  const detailTransform = (form) => {
    const selected = findItem(form.itemId);
    return {
      ...form,
      itemId: form.itemId ?? "",
      unitPrice: selected?.unitPrice ?? form.unitPrice ?? 0,
      paymentAmount: calculateAmount(form.paymentQuantity, selected?.unitPrice ?? form.unitPrice),
    };
  };

  const initialSectionId = dialog?.data?.sectionId
    || getId(sections[0], "subcontractSectionId")
    || "";
  const initialScopeOptions = getScopeOptions(initialSectionId);
  const initialScopeId = dialog?.data?.scopeId || initialScopeOptions[0]?.value || "";
  const getRemainingPaymentQuantity = (itemId) => {
    const selectedItem = findItem(itemId);
    const subcontractQuantity = toNumber(
      selectedItem?.subcontractQuantity ?? selectedItem?.SubcontractQuantity,
    );
    const editingPaymentItemId = dialog?.data?.paymentItemId ?? dialog?.data?.PaymentItemId;
    const paidQuantity = allPaymentItems.reduce((sum, paymentItem) => {
      const paymentItemId = paymentItem.paymentItemId ?? paymentItem.PaymentItemId;
      const paymentItemItemId = paymentItem.itemId ?? paymentItem.ItemId;
      if (String(paymentItemItemId) !== String(itemId)) return sum;
      if (editingPaymentItemId != null && String(paymentItemId) === String(editingPaymentItemId)) return sum;
      return sum + toNumber(paymentItem.paymentQuantity ?? paymentItem.PaymentQuantity);
    }, 0);

    return Math.max(Math.round((subcontractQuantity - paidQuantity) * 10000) / 10000, 0);
  };
  const validatePaymentQuantity = (value, form) => {
    const quantity = Number(value);
    if (!Number.isFinite(quantity) || quantity <= 0) return "Khối lượng phải lớn hơn 0";

    const itemId = form.itemId ?? itemIdOf(dialog?.data);
    const remainingQuantity = getRemainingPaymentQuantity(itemId);
    return quantity > remainingQuantity
      ? `Khối lượng thanh toán không được lớn hơn khối lượng giao khoán còn lại (${remainingQuantity})`
      : "";
  };
  const detailFields = (form) => [
    { name: "scopeId", label: "Phân cấp", options: getScopeOptions(form.sectionId || initialSectionId), full: true, required: true },
    { name: "itemId", label: "Dự toán", options: getItemOptions(form.scopeId || initialScopeId), autocomplete: true, full: true, required: true, placeholder: "Tìm theo mã hoặc tên dự toán..." },
    { name: "paymentQuantity", label: "Khối lượng thanh toán", type: "number", required: true, inputProps: { min: 0.0001, step: "any" }, validate: validatePaymentQuantity },
    { name: "unitPrice", label: "Đơn giá", type: "number", disabled: true, required: true },
    { name: "paymentAmount", label: "Tổng số tiền thanh toán", type: "number", disabled: true, full: true },
    { name: "notes", label: "Ghi chú", multiline: true, full: true },
  ];

  return (
    <>
      <SectionCard
        title="Danh sách kỳ thanh toán"
        action={canCreate ? (
          <Tooltip title={!isSubcontractApproved ? "Chỉ được thêm kỳ thanh toán khi giao khoán đã được phê duyệt" : ""}>
            <span>
              <AppButton
                variant="contained"
                startIcon={<Add />}
                disabled={!isSubcontractApproved}
                onClick={() => setDialog({ type: "profile" })}
              >
                Thêm kỳ thanh toán
              </AppButton>
            </span>
          </Tooltip>
        ) : null}
      >
        <DataTable
          columns={columns}
          data={payments}
          enableExpanding
          renderDetailPanel={({ row }) => (
            <Box sx={{ p: 2, bgcolor: "#f8fafc" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between",}}>
                <strong>Chi tiết {row.original.paymentName}</strong>
                {canCreate && <AppButton size="small" startIcon={<Add />} onClick={() => setDialog({ type: "detail", profile: row.original })}>Thêm chi tiết</AppButton>}
              </Box>
              <Divider />
              {groupPaymentItemsByScope(row.original.items || []).length === 0 ? (
                <Alert severity="info">Chưa có chi tiết thanh toán.</Alert>
              ) : groupPaymentItemsByScope(row.original.items || []).map((group) => (
                <Box key={group.key} sx={{ mt: 2 }}>
                  <Box sx={{ mb: 1 }}>
                    <Typography fontWeight={750}>{group.label}</Typography>
                    {group.subtitle && (
                      <Typography sx={{ fontSize: '12px',fontStyle: 'italic' }} color="text.secondary">{group.subtitle}</Typography>
                    )}
                  </Box>
                  <DataTable
                    columns={[...detailIdentityColumns, ...itemColumns.filter((column) => column.id !== "item")]}
                    data={group.items}
                  />
                </Box>
              ))}
            </Box>
          )}
          enableRowActions={canUpdate || canDelete}
          renderRowActions={({ row }) => (
            <Box>
              {canUpdate && (
                <Tooltip title="Chỉnh sửa">
                  <IconButton size="small" onClick={() => setDialog({ type: "profile", data: row.original })}>
                    <EditOutlined fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDelete && (
                <Tooltip title="Xóa">
                  <IconButton size="small" color="error" onClick={async () => { await handleDeletePaymentProfile(row.original.paymentProfileId); }}>
                    <DeleteOutline fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          )}
        />
      </SectionCard>

      <Paper
        sx={{
          mt: 3,
          p: 3,
          border: "1px solid #e2e8f0",
          borderRadius: 3,
          boxShadow: "none",
          bgcolor: "#f8fafc",
        }}
      >
        <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#0f172a", mb: 2 }}>
          Tổng quan thanh toán
        </Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
            gap: 2,
          }}
        >
          {[
            {
              title: "Giá trị giao khoán",
              color: "#2563eb",
              rows: [
                { label: "Tổng cộng", value: formatCurrencyWithUnit(subcontractTotalBeforeTax) },
              ],
            },
            {
              title: "Đã thanh toán",
              color: "#16a34a",
              rows: [
                { label: "Tổng cộng", value: formatCurrencyWithUnit(totalPaidBeforeTax) },
              ],
            },
            {
              title: "Còn lại",
              color: "#ea580c",
              rows: [
                { label: "Tổng cộng", value: formatCurrencyWithUnit(remainingBeforeTax) },
              ],
            },
          ].map((section) => (
            <Box
              key={section.title}
              sx={{
                p: 2,
                borderRadius: 2,
                border: "1px solid #dbe4ee",
                bgcolor: "#fff",
              }}
            >
              <Typography sx={{ fontSize: "0.92rem", fontWeight: 700, color: section.color, mb: 1.5 }}>
                {section.title}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                {section.rows.map((row) => (
                  <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                    <Typography sx={{ fontSize: "0.9rem", color: "#64748b" }}>{row.label}</Typography>
                    <Typography sx={{ fontSize: "0.92rem", fontWeight: 600, color: "#0f172a", textAlign: "right" }}>
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      </Paper>

      <EntityFormDialog
        open={dialog?.type === "profile"}
        onClose={() => setDialog(null)}
        title={dialog?.data ? "Cập nhật kỳ thanh toán" : "Thêm kỳ thanh toán"}
        initialData={{
          vat: subcontractVatRate,
          ...dialog?.data,
        }}
        onSubmit={saveProfile}
        fields={[
          { name: "paymentName", label: "Tên kỳ thanh toán", full: true, required: true },
          { name: "paymentDate", label: "Ngày thanh toán", type: "date", required: true, defaultToday: true },
          { name: "notes", label: "Ghi chú", multiline: true, full: true },
        ]}
      />
      <EntityFormDialog
        open={dialog?.type === "detail"}
        onClose={() => setDialog(null)}
        title={dialog?.data ? "Cập nhật chi tiết thanh toán" : "Thêm chi tiết thanh toán"}
        initialData={dialog?.data ? enrichPaymentItem(dialog.data) : { sectionId: initialSectionId }}
        transformForm={detailTransform}
        onSubmit={saveItem}
        fields={detailFields}
      />
      <AppSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
      />
    </>
  );
}
